import { useContext } from 'react';

import {
  CollapsibleSection,
  Header,
  Key,
  BubbleKey,
  Row,
  Rows,
  Section,
  Specifications as List,
  Subheader,
  Value
} from '../Specifications';

import { BandStateContext } from '../../../contexts/band';

import * as FedExDeliveryContent from '../../popups/content/FedExDelivery';
import * as TotalBandCaratWeightContent from '../../popups/content/specifications/band/TotalCaratWeight';
import * as MetalWeightContent from '../../popups/content/specifications/band/MetalWeight';
import * as BaseDepthContent from '../../popups/content/specifications/band/BaseDepth';
import * as BaseWidthContent from '../../popups/content/specifications/band/BaseWidth';
import * as SizesAvailableContent from '../../popups/content/specifications/band/SizesAvailable';

import * as NumberContent from '../../popups/content/specifications/accent_stones/Number';
import * as ShapeContent from '../../popups/content/specifications/accent_stones/Shape';
import * as TotalAccentCaratWeightContent from '../../popups/content/specifications/accent_stones/TotalCaratWeight';
import * as ColorContent from '../../popups/content/specifications/accent_stones/Color';
import * as ClarityContent from '../../popups/content/specifications/accent_stones/Clarity';

import ColoredFedEx from '../../ColoredFedEx';

import type { TBandDetail } from '@types/band';
import type { ObjectType } from '../../../types/misc';

const shankThickness = band => {
  if (band.shank_thickness) {
    return `${Number(band.shank_thickness).toFixed(2)} mm`;
  }
};

const baseWidth = band => {
  if (band.shank_base_width) {
    return `${Number(band.shank_base_width).toFixed(2)} mm`;
  }
};

const metalWeight = band => {
  if (band.gram_weight) {
    return `${Number(band.gram_weight).toFixed(2)} gm`;
  }
};

function MainSection({ band }: { band: TBandDetail }) {
  const shankThicknessValue = shankThickness(band);
  const baseWidthValue = baseWidth(band);

  return (
    <Section>
      <Header>Setting Details</Header>
      <Rows>
        <Row>
          <Key>Item Number</Key>
          <Value>{band.item_number}</Value>
        </Row>
        {band.metal && (
          <Row>
            <Key>Metal</Key>
            <Value>{band.metal}</Value>
          </Row>
        )}
        {band.total_carat_weight ? (
          <Row>
            <BubbleKey content={TotalBandCaratWeightContent}>
              Total Ct. Wt.
            </BubbleKey>
            <Value>{Number(band.total_carat_weight).toFixed(2)} ct.</Value>
          </Row>
        ) : null}
        {band.gram_weight ? (
          <Row>
            <BubbleKey content={MetalWeightContent}>Metal Weight</BubbleKey>
            <Value>{metalWeight(band)}</Value>
          </Row>
        ) : null}
        {shankThicknessValue ? (
          <Row>
            <BubbleKey content={BaseDepthContent}>Base Depth</BubbleKey>
            <Value>{shankThicknessValue}</Value>
          </Row>
        ) : null}
        {shankThicknessValue ? (
          <Row>
            <BubbleKey content={BaseWidthContent}>Base Width</BubbleKey>
            <Value>{baseWidthValue}</Value>
          </Row>
        ) : null}
        {band.size_range ? (
          <Row>
            <BubbleKey content={SizesAvailableContent}>
              Sizes Available
            </BubbleKey>
            <Value>{band.size_range}</Value>
          </Row>
        ) : null}
      </Rows>
    </Section>
  );
}

function AccentStonesSection({
  stones,
  showNumber,
  number
}: {
  stones: ObjectType,
  number: number,
  showNumber: boolean
}) {
  return (
    <CollapsibleSection>
      <Subheader>Accent Diamonds{showNumber && ` (Group ${number})`}</Subheader>
      <Rows>
        <Row>
          <BubbleKey content={NumberContent}>Number</BubbleKey>
          <Value>{stones.quantity}</Value>
        </Row>
        <Row>
          <BubbleKey content={ShapeContent}>Shape</BubbleKey>
          <Value>{stones.shape}</Value>
        </Row>
        <Row>
          <BubbleKey content={TotalAccentCaratWeightContent}>
            Total Ct. Wt.
          </BubbleKey>
          <Value>{stones.total_carat_weight_string}</Value>
        </Row>
        {stones.minimum_color && (
          <Row>
            <BubbleKey content={ColorContent}>Min. Color</BubbleKey>
            <Value>{stones.minimum_color}</Value>
          </Row>
        )}
        {stones.minimum_clarity && (
          <Row>
            <BubbleKey content={ClarityContent}>Min. Clarity</BubbleKey>
            <Value>{stones.minimum_clarity}</Value>
          </Row>
        )}
      </Rows>
    </CollapsibleSection>
  );
}

function OrderDetailsSection({ band }: { band: TBandDetail }) {
  return (
    <Section>
      <Rows>
        <Row>
          <Key>Returns</Key>
          <Value>
            <span>30 days</span>
          </Value>
        </Row>
        <Row>
          <Key>Comes with</Key>
          <Value>
            Appraisal
            <br />
            Presentation box
          </Value>
        </Row>
        <Row>
          <BubbleKey content={FedExDeliveryContent}>Shipping</BubbleKey>
          <Value>
            <ColoredFedEx />
          </Value>
        </Row>
        <Row>
          <Key>Delivery Date</Key>
          <Value>
            <span>{band.delivery_date}</span>
          </Value>
        </Row>
      </Rows>
    </Section>
  );
}

export default function Specs() {
  const { band }: { band: TBandDetail } = useContext(BandStateContext);

  if (!band) return null;

  return (
    <List allowExpansion={band.preset_stones && band.preset_stones.length > 0}>
      <MainSection band={band} />
      {band.preset_stones
        ? band.preset_stones.map((ps: ObjectType, i: number) => {
            return (
              <AccentStonesSection
                stones={ps}
                key={`stones_${i}`}
                number={i + 1}
                showNumber={band.preset_stones.length > 1}
              />
            );
          })
        : null}
      <OrderDetailsSection band={band} />
    </List>
  );
}
